// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module Privacy
 * @package @geenius/analytics
 * @description Privacy helpers for masking, anonymizing, or redacting
 * analytics payloads before they leave the shared runtime. These helpers power
 * tracker-level PII controls across all published variants.
 */

import type {
  AnalyticsEvent,
  ConsentSettings,
  PrivacyMode,
  UserTraits,
} from "./types";

const SENSITIVE_KEYS = new Set([
  "email",
  "name",
  "firstName",
  "lastName",
  "fullName",
  "phone",
  "mobile",
  "address",
  "company",
  "avatar",
  "ip",
  "ipAddress",
  "userId",
  "sessionId",
]);
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /(?:\+?\d[\d\s().-]{7,}\d)/g;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const QUERY_PARAM_PATTERN = /([?&])([^=&#]+)=([^&#]*)/g;

export function hashString(value: string): string {
  let hash = 5381;
  for (let index = 0; index < value.length; index++) {
    hash = ((hash << 5) + hash) ^ value.charCodeAt(index);
  }
  return Math.abs(hash).toString(36);
}

export function maskString(value: string): string {
  if (!value) return value;
  if (value.includes("@")) {
    const [local, domain] = value.split("@");
    return `${(local ?? "").slice(0, 1)}***@${domain ?? ""}`;
  }
  if (value.length <= 4) return "****";
  return `${value.slice(0, 1)}${"*".repeat(Math.max(1, value.length - 2))}${value.slice(-1)}`;
}

function maskEmbeddedPii(value: string): string {
  return value
    .replace(EMAIL_PATTERN, (match) => maskString(match))
    .replace(PHONE_PATTERN, (match) => maskString(match));
}

function sanitizeEmbeddedPii(value: string, mode: PrivacyMode): string {
  return value
    .replace(EMAIL_PATTERN, (match) => sanitizeText(match, mode))
    .replace(PHONE_PATTERN, (match) => sanitizeText(match, mode));
}

function normalizeSensitiveKey(key: string): string {
  return key.replace(/[-_\s]/g, "").toLowerCase();
}

function isSensitiveKey(key: string): boolean {
  const normalizedKey = normalizeSensitiveKey(key);
  for (const sensitiveKey of SENSITIVE_KEYS) {
    if (normalizeSensitiveKey(sensitiveKey) === normalizedKey) {
      return true;
    }
  }
  return false;
}

function isIpAddressKey(key: string): boolean {
  const normalizedKey = normalizeSensitiveKey(key);
  return normalizedKey === "ip" || normalizedKey === "ipaddress";
}

function isIdentifierKey(key: string): boolean {
  const normalizedKey = normalizeSensitiveKey(key);
  return normalizedKey === "userid" || normalizedKey === "sessionid";
}

function getIdentifierPrefix(key: string): string {
  const normalizedKey = normalizeSensitiveKey(key);
  if (normalizedKey === "userid") return "user";
  if (normalizedKey === "sessionid") return "session";
  return "id";
}

function decodeQueryValue(value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, " "));
  } catch {
    return value;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function sanitizeText(value: string, mode: PrivacyMode): string {
  if (mode === "redact") return "[redacted]";
  if (mode === "anonymize") return `anon_${hashString(value)}`;
  return maskEmbeddedPii(maskString(value));
}

function sanitizeIpAddress(value: string, mode: PrivacyMode): string {
  if (mode === "redact") return "[redacted]";
  if (mode === "anonymize") return `anon_${hashString(value)}`;
  return truncateIpAddress(value);
}

function sanitizeIdentifier(
  value: string,
  mode: PrivacyMode,
  key: string,
): string {
  if (mode === "redact") return "[redacted]";
  if (mode === "anonymize") return pseudonymizeIdentifier(value, "anon");
  return pseudonymizeIdentifier(value, getIdentifierPrefix(key));
}

function sanitizeMetadataText(value: string, mode: PrivacyMode): string {
  return sanitizeEmbeddedPii(value, mode).replace(
    QUERY_PARAM_PATTERN,
    (match, prefix: string, key: string, entry: string) => {
      if (!isSensitiveKey(key)) return match;
      const sanitizedEntry = sanitizeSensitiveValue(
        decodeQueryValue(entry),
        mode,
        key,
      );
      return `${prefix}${key}=${String(sanitizedEntry)}`;
    },
  );
}

function sanitizeValue(value: unknown, mode: PrivacyMode): unknown {
  if (Array.isArray(value)) {
    return value.map((item) =>
      isPlainObject(item) ? sanitizeObject(item, mode) : item,
    );
  }
  if (isPlainObject(value)) {
    return sanitizeObject(value, mode);
  }
  if (typeof value === "string") {
    return sanitizeEmbeddedPii(value, mode);
  }
  return value;
}

function sanitizeSensitiveValue(
  value: unknown,
  mode: PrivacyMode,
  key?: string,
): unknown {
  if (typeof value === "string") {
    if (key && isIpAddressKey(key)) return sanitizeIpAddress(value, mode);
    if (key && isIdentifierKey(key)) return sanitizeIdentifier(value, mode, key);
    return sanitizeText(value, mode);
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeSensitiveValue(item, mode, key));
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        sanitizeSensitiveValue(entry, mode, key),
      ]),
    );
  }
  if (mode === "redact") return "[redacted]";
  if (mode === "anonymize") return "[anonymized]";
  return "[masked]";
}

/**
 * Sanitizes a generic object by masking, anonymizing, or redacting sensitive fields.
 *
 * @param value Object to sanitize before transport or storage.
 * @param mode Sanitization strategy to apply to sensitive fields and nested values.
 * @returns A sanitized copy of the supplied object.
 */
export function sanitizeObject(
  value: Record<string, unknown>,
  mode: PrivacyMode = "mask",
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (isSensitiveKey(key)) {
      result[key] = sanitizeSensitiveValue(entry, mode, key);
      continue;
    }

    result[key] = sanitizeValue(entry, mode);
  }
  return result;
}

/**
 * Sanitizes arbitrary user traits while preserving the published user-traits shape.
 *
 * @param traits Optional user traits to sanitize.
 * @param mode Sanitization strategy to apply to nested values.
 * @returns A sanitized copy of the supplied traits, or `undefined`.
 */
export function sanitizeUserTraits(
  traits: UserTraits | undefined,
  mode: PrivacyMode = "mask",
): UserTraits | undefined {
  if (!traits) return undefined;
  return {
    ...traits,
    ...sanitizeObject(traits, mode),
  };
}

/**
 * Sanitizes an analytics event payload before it is dispatched to providers.
 *
 * @param event Analytics event to sanitize.
 * @param mode Sanitization strategy to apply to event identifiers and properties.
 * @returns A sanitized analytics event payload.
 */
export function sanitizeAnalyticsEvent(
  event: AnalyticsEvent,
  mode: PrivacyMode = "mask",
): AnalyticsEvent {
  return {
    ...event,
    userId: event.userId
      ? sanitizeIdentifier(event.userId, mode, "userId")
      : undefined,
    ipAddress:
      typeof event.ipAddress === "string"
        ? sanitizeIpAddress(event.ipAddress, mode)
        : event.ipAddress,
    properties: event.properties
      ? sanitizeObject(event.properties, mode)
      : event.properties,
    referrer: sanitizeMetadataText(event.referrer, mode),
    url: sanitizeMetadataText(event.url, mode),
  };
}

/**
 * Sanitizes a page-view-like payload while preserving the incoming object shape.
 *
 * @param pageView Page-view record to sanitize.
 * @param mode Sanitization strategy to apply to URLs, referrers, and user identity.
 * @returns A sanitized copy of the supplied page-view object.
 */
export function sanitizePageView<
  T extends {
    url: string;
    referrer: string;
    userId?: string;
    title?: string;
    ipAddress?: string;
    properties?: Record<string, unknown>;
  },
>(pageView: T, mode: PrivacyMode = "mask"): T {
  const sanitizedPageView = {
    ...pageView,
    userId: pageView.userId
      ? sanitizeIdentifier(pageView.userId, mode, "userId")
      : undefined,
    url: sanitizeMetadataText(pageView.url, mode),
    referrer: sanitizeMetadataText(pageView.referrer, mode),
    ipAddress:
      typeof pageView.ipAddress === "string"
        ? sanitizeIpAddress(pageView.ipAddress, mode)
        : pageView.ipAddress,
    title: pageView.title
      ? sanitizeMetadataText(pageView.title, mode)
      : pageView.title,
    properties: pageView.properties
      ? sanitizeObject(pageView.properties, mode)
      : pageView.properties,
  };

  // Preserve the caller's extended page-view shape while only rewriting published fields.
  return sanitizedPageView as T;
}

/**
 * Reports whether an object contains keys treated as privacy-sensitive.
 *
 * @param value Object to inspect.
 * @returns `true` when a known sensitive key is present.
 */
export function hasSensitiveKeys(value: Record<string, unknown>): boolean {
  return Object.keys(value).some((key) => isSensitiveKey(key));
}

/**
 * Reports whether browser-level Do Not Track is enabled.
 *
 * @returns `true` when the current navigator advertises DNT.
 */
export function isDoNotTrackEnabled(): boolean {
  if (typeof navigator === "undefined") return false;
  return navigator.doNotTrack === "1" || navigator.doNotTrack === "yes";
}

/**
 * Builds a canonical consent settings payload with the public category names.
 *
 * @param overrides Optional category overrides for the consent payload.
 * @returns Consent settings with essential enabled and unset categories denied.
 */
export function createConsentSettings(
  overrides: Partial<ConsentSettings> = {},
): ConsentSettings {
  const personalization =
    overrides.personalization ?? overrides.functional ?? false;
  return {
    essential: overrides.essential ?? true,
    analytics: overrides.analytics ?? false,
    marketing: overrides.marketing ?? false,
    personalization,
    functional: overrides.functional ?? personalization,
    timestamp: overrides.timestamp ?? Date.now(),
  };
}

/**
 * Checks whether a consent category is granted.
 *
 * @param settings Current consent settings, or `null` when not recorded.
 * @param category Category to check.
 * @returns `true` when the category is granted.
 */
export function hasConsentFor(
  settings: ConsentSettings | null | undefined,
  category: "essential" | "analytics" | "marketing" | "personalization",
): boolean {
  if (!settings) return category === "essential";
  return settings[category] === true;
}

/**
 * Determines whether a timestamp is older than the configured retention window.
 *
 * @param timestamp Event timestamp in milliseconds.
 * @param retentionDays Number of days to retain data.
 * @param now Current timestamp used for deterministic tests.
 * @returns `true` when the record should be purged.
 */
export function shouldPurgeByRetention(
  timestamp: number,
  retentionDays: number,
  now = Date.now(),
): boolean {
  if (retentionDays <= 0) return true;
  return now - timestamp > retentionDays * DAY_IN_MS;
}

/**
 * Computes the oldest timestamp allowed by a retention window.
 *
 * @param retentionDays Number of days analytics data should be retained.
 * @param now Current timestamp used for deterministic tests.
 * @returns Cutoff timestamp, or `null` when no retention window is configured.
 */
export function getRetentionCutoffTimestamp(
  retentionDays: number | undefined,
  now = Date.now(),
): number | null {
  if (retentionDays === undefined) return null;
  if (!Number.isFinite(retentionDays)) return null;
  if (retentionDays <= 0) return now;
  return now - retentionDays * DAY_IN_MS;
}

/**
 * Filters event and page-view records to the configured retention window.
 *
 * @param records Timestamped analytics records to filter.
 * @param retentionDays Number of days analytics data should be retained.
 * @param now Current timestamp used for deterministic tests.
 * @returns Records that are still inside the retention window.
 */
export function filterRetainedAnalyticsRecords<T extends { timestamp: number }>(
  records: readonly T[],
  retentionDays: number | undefined,
  now = Date.now(),
): T[] {
  if (retentionDays === undefined || !Number.isFinite(retentionDays)) {
    return [...records];
  }

  return records.filter(
    (record) => !shouldPurgeByRetention(record.timestamp, retentionDays, now),
  );
}

type RetentionAggregateTimestampKey =
  | "timestamp"
  | "generatedAt"
  | "lastUpdated"
  | "analyzedAt"
  | "lastActivity";

type RetentionAggregate = Partial<
  Record<RetentionAggregateTimestampKey, number>
>;

function getAggregateTimestamp(
  aggregate: RetentionAggregate,
): number | undefined {
  return (
    aggregate.timestamp ??
    aggregate.generatedAt ??
    aggregate.lastUpdated ??
    aggregate.analyzedAt ??
    aggregate.lastActivity
  );
}

/**
 * Filters aggregate snapshots to the configured retention window.
 *
 * Aggregates without a known timestamp are preserved because the shared runtime
 * cannot prove they are expired.
 *
 * @param aggregates Aggregate snapshots to filter.
 * @param retentionDays Number of days analytics data should be retained.
 * @param now Current timestamp used for deterministic tests.
 * @returns Aggregates that are still inside the retention window.
 */
export function filterRetainedAnalyticsAggregates<T extends RetentionAggregate>(
  aggregates: readonly T[],
  retentionDays: number | undefined,
  now = Date.now(),
): T[] {
  if (retentionDays === undefined || !Number.isFinite(retentionDays)) {
    return [...aggregates];
  }

  return aggregates.filter((aggregate) => {
    const timestamp = getAggregateTimestamp(aggregate);
    return (
      timestamp === undefined ||
      !shouldPurgeByRetention(timestamp, retentionDays, now)
    );
  });
}

/**
 * Truncates an IPv4 or IPv6 address for analytics storage.
 *
 * @param ip Address to truncate.
 * @returns Truncated IP address, or the original value when unsupported.
 */
export function truncateIpAddress(ip: string): string {
  if (ip.includes(".")) {
    const parts = ip.split(".");
    return parts.length === 4 ? `${parts.slice(0, 3).join(".")}.0` : ip;
  }

  if (ip.includes(":")) {
    const parts = ip.split(":");
    return parts.length > 2 ? `${parts.slice(0, 4).join(":")}::` : ip;
  }

  return ip;
}

/**
 * Produces a stable pseudonymous identifier for analytics records.
 *
 * @param value Source identifier to pseudonymize.
 * @param prefix Prefix used for the returned identifier.
 * @returns Stable pseudonymous identifier.
 */
export function pseudonymizeIdentifier(value: string, prefix = "anon"): string {
  return `${prefix}_${hashString(value)}`;
}

/**
 * Convenience object that groups the shared privacy helpers under one namespace.
 */
export const privacyUtils = {
  sanitizeObject,
  sanitizeUserTraits,
  sanitizeAnalyticsEvent,
  sanitizePageView,
  hasSensitiveKeys,
  isDoNotTrackEnabled,
  createConsentSettings,
  hasConsentFor,
  shouldPurgeByRetention,
  getRetentionCutoffTimestamp,
  filterRetainedAnalyticsRecords,
  filterRetainedAnalyticsAggregates,
  truncateIpAddress,
  hashString,
  maskString,
  pseudonymizeIdentifier,
};
